import type { SchoolClass } from "p6m-p6u";
import { ClassActivityCategory } from "../../constants/ClassActivityConstants";
import type { useTestOperations } from "../../hooks/useTestOperations";
import type { useVocabTrainingOperations } from "../../hooks/useVocabTrainingOperations";
import type { ShareFlowPopupTestProps, ShareFlowPopupVocabProps } from "./ShareFlowPopup";

/** Wires the test share flow to the popup — single source for production and the jest harnesses. */
export const buildTestShareFlowPopupProps = (
    testOps: ReturnType<typeof useTestOperations>,
    schoolClasses: SchoolClass[]
): ShareFlowPopupTestProps => ({
    isOpen: !!testOps.sharePopup.testId,
    onClose: testOps.handleCloseSharePopup,
    classActivityCategory: ClassActivityCategory.TEST,
    step: testOps.sharePopup.step,
    onPrimaryAction: testOps.handleSharePrimaryAction,
    onPrevStep: testOps.handleSharePrevStep,
    isFinalAction: testOps.isFinalShareAction,
    shareInProgress: testOps.shareInProgress,
    selectedSchoolClassId: testOps.sharePopup.selectedSchoolClassId,
    onSelectClass: testOps.handleSelectShareClass,
    schoolClasses,
    newClassForm: testOps.sharePopup.newClassForm,
    onOpenNewClassForm: testOps.handleOpenNewClassForm,
    onCloseNewClassForm: testOps.handleCloseNewClassForm,
    onCollapseNewClassForm: testOps.handleCollapseNewClassForm,
    onNewClassNameChange: testOps.handleNewClassNameChange,
    onToggleNewClassConsent: testOps.handleToggleNewClassConsent,
    selectedTiming: testOps.sharePopup.timing,
    onSelectTiming: testOps.handleSelectShareTiming,
    visibilityDateValue: testOps.sharePopup.visibilityDate,
    onVisibilityDateChange: testOps.handleShareVisibilityDateChange,
    selectedMode: testOps.sharePopup.selectedMode,
    onSelectMode: testOps.handleSelectShareMode,
    testName: testOps.sharePopup.testName,
    infoExpanded: testOps.sharePopup.infoExpanded,
    onToggleInfo: testOps.handleToggleShareInfo,
    message: testOps.sharePopup.message,
    showDeadline: testOps.sharePopup.deadline.show,
    deadlineValue: testOps.sharePopup.deadline.value,
    showDuration: testOps.sharePopup.duration.show,
    durationHours: testOps.sharePopup.duration.hours.display,
    durationMinutes: testOps.sharePopup.duration.minutes.display,
    ...testOps.shareDetailHandlers,
});

/** Wires the vocab-training share flow to the popup — single source for production and the jest harness. */
export const buildVocabShareFlowPopupProps = (
    vocabTrainingOps: ReturnType<typeof useVocabTrainingOperations>,
    schoolClasses: SchoolClass[]
): ShareFlowPopupVocabProps => ({
    isOpen: !!vocabTrainingOps.sharePopup.trainingId,
    onClose: vocabTrainingOps.handleCloseSharePopup,
    classActivityCategory: ClassActivityCategory.VOCAB_TRAINING,
    step: vocabTrainingOps.sharePopup.step,
    onPrimaryAction: vocabTrainingOps.handleSharePrimaryAction,
    onPrevStep: vocabTrainingOps.handleSharePrevStep,
    isFinalAction: vocabTrainingOps.isFinalShareAction,
    shareInProgress: vocabTrainingOps.shareInProgress,
    selectedSchoolClassId: vocabTrainingOps.sharePopup.selectedSchoolClassId,
    onSelectClass: vocabTrainingOps.handleSelectShareClass,
    schoolClasses,
    newClassForm: vocabTrainingOps.sharePopup.newClassForm,
    onOpenNewClassForm: vocabTrainingOps.handleOpenNewClassForm,
    onCloseNewClassForm: vocabTrainingOps.handleCloseNewClassForm,
    onCollapseNewClassForm: vocabTrainingOps.handleCollapseNewClassForm,
    onNewClassNameChange: vocabTrainingOps.handleNewClassNameChange,
    onToggleNewClassConsent: vocabTrainingOps.handleToggleNewClassConsent,
    selectedTiming: vocabTrainingOps.sharePopup.timing,
    onSelectTiming: vocabTrainingOps.handleSelectShareTiming,
    visibilityDateValue: vocabTrainingOps.sharePopup.visibilityDate,
    onVisibilityDateChange: vocabTrainingOps.handleShareVisibilityDateChange,
});
